import { Fragment } from 'react'
import { translate, type Lang } from './i18n'
import type { Trends, TrendPoint, TrendTotals } from './api'

/** Cuánto cambió contra el periodo anterior, o nada si no hay con qué comparar. */
function Delta({ lang, now, before, invert = false }: {
  lang: Lang
  now: number | null
  before: number | null | undefined
  /** En diferidas y desvíos, bajar es mejorar. */
  invert?: boolean
}) {
  if (now === null || before === null || before === undefined) return null

  const cambio = now - before
  // Un cambio de menos de medio punto porcentual es ruido, no tendencia.
  if (Math.abs(cambio) < 0.005 && Math.abs(cambio) < 1) {
    return <span className="meta"> · {translate(lang, 'trends.flat')}</span>
  }

  const mejor = invert ? cambio < 0 : cambio > 0
  const texto = Math.abs(now) <= 1 && Math.abs(before) <= 1
    ? `${cambio > 0 ? '+' : '−'}${Math.abs(Math.round(cambio * 100))} pts`
    : `${cambio > 0 ? '+' : '−'}${Math.abs(Math.round(cambio))}`

  return <span className={mejor ? 'win' : 'error'}> · {texto}</span>
}

/**
 * La serie del periodo, en barras.
 *
 * Sin biblioteca de gráficas: son dos docenas de barras y una escala de 0 a 1. Traer un paquete
 * entero para esto costaría más de lo que resuelve, y además hay que poder leerlo con el teclado,
 * cosa que las bibliotecas suelen olvidar.
 */
function Series({ lang, series }: { lang: Lang; series: TrendPoint[] }) {
  if (series.length === 0) return null

  const ancho = 100 / series.length
  const cuando = (p: TrendPoint) => `${p.shiftDate} · ${p.shiftCode}`

  return (
    <div className="card">
      <h3>{translate(lang, 'trends.series')}</h3>
      <p className="notice">{translate(lang, 'trends.seriesHelp')}</p>

      <svg className="serie" viewBox="0 0 100 34" preserveAspectRatio="none" role="img"
        aria-label={translate(lang, 'trends.series')}>
        {/* La mitad, para tener contra qué mirar la altura de cada barra. */}
        <line x1="0" y1="17" x2="100" y2="17" className="guia" />
        {series.map((p, i) => {
          const alto = (p.onTimeRate ?? 0) * 30
          return (
            <rect
              key={cuando(p)}
              x={i * ancho + ancho * 0.15}
              y={32 - alto}
              width={ancho * 0.7}
              height={Math.max(alto, 0.4)}
              className={p.deferred > 0 ? 'barra aviso' : 'barra'}
            >
              <title>
                {cuando(p)} · {translate(lang, 'trends.onTime')} {pct(p.onTimeRate)} · {translate(lang, 'insight.deferred')} {p.deferred}
              </title>
            </rect>
          )
        })}
      </svg>

      <div className="row-line">
        <span className="meta">{series[0] ? cuando(series[0]) : ''}</span>
        <div className="spacer" />
        <span className="meta">{series.at(-1) ? cuando(series.at(-1)!) : ''}</span>
      </div>
    </div>
  )
}

const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`)

/**
 * Tendencias: cómo venimos (§11).
 *
 * Un turno solo no dice nada porque todos tienen un mal día. Lo que decide algo es la repetición,
 * y por eso todo aquí se compara contra el periodo anterior y se corta por donde la repetición
 * suele esconderse: el turno del día, la división, el punto del mapa.
 */
export function TrendsView({ lang, trends, onExport, onReport, busy }: {
  lang: Lang
  trends: Trends | null
  busy: boolean
  onExport: () => void
  /** Abre el reporte del periodo, pensado para imprimir o presentar. */
  onReport: () => void
}) {
  if (!trends) return null

  const t = trends.totals
  const antes: TrendTotals | null = trends.previous

  if (t.shifts === 0) {
    return <div className="card"><p className="notice">{translate(lang, 'insight.empty')}</p></div>
  }

  return (
    <>
      <div className="card">
        <div className="row-line">
          <strong>{translate(lang, 'trends.period', [trends.from, trends.to, t.shifts])}</strong>
          <div className="spacer" />
          <button disabled={busy} onClick={onExport}>{translate(lang, 'trends.export')}</button>
          {/* Dos salidas distintas y a propósito juntas: la hoja sirve para volver a analizar,
              el reporte para llevar a una junta. Confundirlas fue lo que dejó a INSIGHT sin
              nada presentable durante todo este tiempo. */}
          <button disabled={busy} onClick={onReport}>{translate(lang, 'report.open')}</button>
        </div>
        <p className="notice">
          {antes
            ? translate(lang, 'trends.against', [antes.shifts])
            : translate(lang, 'trends.noPrevious')}
        </p>
      </div>

      <div className="grid">
        <div className="card">
          <h3>{translate(lang, 'trends.compliance')}</h3>
          <div className="meta">
            {translate(lang, 'trends.onTime')}: <strong>{pct(t.onTimeRate)}</strong>
            <Delta lang={lang} now={t.onTimeRate} before={antes?.onTimeRate} />
          </div>
          <div className="meta">
            {translate(lang, 'trends.presence')}: <strong>{pct(t.presenceRate)}</strong>
            <Delta lang={lang} now={t.presenceRate} before={antes?.presenceRate} />
          </div>
          <div className="meta">
            {translate(lang, 'insight.deferred')}: <strong>{t.deferred}</strong>
            <Delta lang={lang} now={t.deferred} before={antes?.deferred} invert />
          </div>
          <div className="meta">
            {translate(lang, 'insight.deviations')}: <strong>{t.deviations}</strong>
            <Delta lang={lang} now={t.deviations} before={antes?.deviations} invert />
          </div>
        </div>

        <div className="card">
          <h3>{translate(lang, 'trends.work')}</h3>
          <div className="meta">{translate(lang, 'trends.planned', [t.planned])}</div>
          <div className="meta">{translate(lang, 'trends.released', [t.released])}</div>
          <div className="meta">{translate(lang, 'trends.completed', [t.completed])}</div>
          <div className="meta">
            {translate(lang, 'trends.incidents', [t.incidents, Math.round(t.incidentMinutes)])}
          </div>
        </div>

        {trends.deviations.length > 0 && (
          <div className="card">
            <h3>{translate(lang, 'trends.kinds')}</h3>
            <p className="notice">{translate(lang, 'trends.kindsHelp')}</p>
            {trends.deviations.map((d) => (
              <div key={d.kind} className="meta">
                {translate(lang, `kind.${d.kind}`)}: <strong>{d.count}</strong>
              </div>
            ))}
          </div>
        )}
      </div>

      <Series lang={lang} series={trends.series} />

      <div className="grid">
        <div className="card">
          <h3>{translate(lang, 'trends.byShift')}</h3>
          <p className="notice">{translate(lang, 'trends.byShiftHelp')}</p>
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'insight.shift')}</th>
                <th>{translate(lang, 'trends.shifts')}</th>
                <th>{translate(lang, 'trends.onTime')}</th>
                <th>{translate(lang, 'trends.presence')}</th>
                <th>{translate(lang, 'insight.deferred')}</th>
              </tr>
            </thead>
            <tbody>
              {trends.byShiftCode.map((s) => (
                <tr key={s.shiftCode}>
                  <td>{s.shiftCode}</td>
                  <td className="num">{s.shifts}</td>
                  <td className="num">{pct(s.onTimeRate)}</td>
                  <td className="num">{pct(s.presenceRate)}</td>
                  <td className={s.deferred > 0 ? 'num error' : 'num'}>{s.deferred}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <h3>{translate(lang, 'trends.byDivision')}</h3>
          <p className="notice">{translate(lang, 'trends.byDivisionHelp')}</p>
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'coverage.division')}</th>
                <th>{translate(lang, 'insight.assignments')}</th>
                <th>{translate(lang, 'trends.onTime')}</th>
                <th>{translate(lang, 'trends.presence')}</th>
              </tr>
            </thead>
            <tbody>
              {trends.byDivision.map((d) => (
                <tr key={d.division || '—'}>
                  <td>{d.division || translate(lang, 'trends.noDivision')}</td>
                  <td className="num">{d.assignments}</td>
                  <td className="num">{pct(d.onTimeRate)}</td>
                  <td className="num">{pct(d.presenceRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {trends.byProfile.length > 0 && (
        <div className="card">
          <h3>{translate(lang, 'trends.byProfile')}</h3>
          <p className="notice">{translate(lang, 'trends.byProfileHelp')}</p>
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'strategy.active')}</th>
                <th>{translate(lang, 'trends.shifts')}</th>
                <th>{translate(lang, 'trends.onTime')}</th>
                <th>{translate(lang, 'trends.presence')}</th>
                <th>{translate(lang, 'insight.deferred')}</th>
              </tr>
            </thead>
            <tbody>
              {trends.byProfile.map((p) => (
                <tr key={p.profile || '—'}>
                  <td>{p.profile || translate(lang, 'trends.noProfile')}</td>
                  <td className="num">{p.shifts}</td>
                  <td className="num">{pct(p.onTimeRate)}</td>
                  <td className="num">{pct(p.presenceRate)}</td>
                  <td className={(p.deferred ?? 0) > 0 ? 'num error' : 'num'}>{p.deferred ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {trends.spots.length > 0 && (
        <div className="card">
          <h3>{translate(lang, 'trends.spots')}</h3>
          <p className="notice">{translate(lang, 'trends.spotsHelp')}</p>
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'trends.spot')}</th>
                <th>{translate(lang, 'trends.visits')}</th>
                <th>{translate(lang, 'trends.lateVisits')}</th>
                <th>{translate(lang, 'insight.late')}</th>
              </tr>
            </thead>
            <tbody>
              {trends.spots.map((s) => (
                <Fragment key={s.label}>
                  <tr>
                    <td>{s.label}</td>
                    <td className="num">{s.visits}</td>
                    <td className="num">{s.late}</td>
                    <td className="num">{Math.round(s.averageLateMinutes)} min</td>
                  </tr>
                  {/* En su propio renglón y debajo del punto: es lo que convierte «llegamos
                      tarde» en algo que alguien puede ir a cambiar. Sin esto, la tabla señala
                      el síntoma y deja al analista adivinando qué perilla tocar. */}
                  <tr>
                    <td colSpan={4} className="meta">
                      {s.causes.length === 0
                        ? translate(lang, 'cause.none')
                        : (
                          <>
                            <strong>{translate(lang, 'cause.title')}:</strong>{' '}
                            {s.causes.map((c, i) => (
                              <span key={c.code}>
                                {i > 0 && ' · '}
                                {translate(lang, `cause.${c.code}`, [Math.round(c.magnitude)])}{' '}
                                {translate(lang, 'cause.cases', [c.cases])}
                              </span>
                            ))}
                            <br />
                            {/* Siempre, no solo cuando hay causas: lo que se está leyendo es una
                                hipótesis, y el producto no afirma causalidad sin metodología. */}
                            <em>{translate(lang, 'cause.hypothesis')}</em>
                          </>
                        )}
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {trends.units.length > 0 && (
        <div className="card">
          <h3>{translate(lang, 'trends.units')}</h3>
          {/* Va al final y con esta advertencia a propósito: no es una calificación de personas. */}
          <p className="notice">{translate(lang, 'trends.unitsHelp')}</p>
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'insight.unit')}</th>
                <th>{translate(lang, 'trends.shifts')}</th>
                <th>{translate(lang, 'insight.assignments')}</th>
                <th>{translate(lang, 'trends.lateVisits')}</th>
                <th>{translate(lang, 'trends.short')}</th>
              </tr>
            </thead>
            <tbody>
              {trends.units.map((u) => (
                <tr key={u.callSign}>
                  <td>{u.callSign}</td>
                  <td className="num">{u.shifts}</td>
                  <td className="num">{u.assignments}</td>
                  <td className="num">{u.late}</td>
                  <td className="num">{u.shortPresence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
